import { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar/Navbar";
import { styled } from "styled-components";
import PageHeader from "../../components/PageHeader/PageHeader";
import Footer from "../../components/Footer/Footer";
import { useNavigate } from "react-router-dom";
import {
  displayPosition,
  localizationStdM,
  Tractor,
  useTractors,
} from "../../contexts/TractorContext";
import Map, { MapVehicle } from "../../components/Map/Map";
import { LatLng } from "leaflet";
import TldStatus from "../../components/TldStatus/TldStatus";
import MultiViewTable, {
  View,
} from "../../components/MultiViewTable/MultiViewTable";
import {
  getLifetimeMetrics,
  getThisWeekMetrics,
  getTodayMetrics,
} from "./performanceIndicatorViews";
import { atlMapBounds, atlMapCenter } from "../../utils/constants";
import { usePermissions } from "../../contexts/PermissionsContext";
import GreyedOutMap from "../../components/GreyedOutMap/GreyedOutMap";

const Dashboard = () => {
  const navigate = useNavigate();
  const { tractors } = useTractors();
  const { hasPermission } = usePermissions();
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 600);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /**
   * Takes in the name -> tractor mapping from TractorContext.tsx and transforms the data into the format Map.tsx expects.
   *
   * @param tractors - The tractors state from TractorContext.tsx.
   * @returns An array of MapVehicles for the Map to render.
   */
  const constructMapVehicles = useCallback(
    (tractors: Map<string, Tractor>): MapVehicle[] => {
      const mapVehicles: MapVehicle[] = [];
      for (const tractor of tractors.values()) {
        // Render any tractor with a known position. Offline tractors carry
        // their last-known coords (hydrated from DynamoDB on backend startup),
        // so the status flag tells the Map to style them distinctly.
        const position = displayPosition(tractor);
        if (position) {
          const status: MapVehicle["status"] = tractor.statuses.online
            ? "online"
            : "offline";
          const newMapVehicle: MapVehicle = {
            id: tractor.displayName,
            label: tractor.displayName,
            position: new LatLng(position.lat, position.lon),
            action: () => navigate(`/vehicles/${tractor.displayName}`),
            status,
            confidenceRadiusM: localizationStdM(tractor),
          };
          mapVehicles.push(newMapVehicle);
        }
      }
      return mapVehicles;
    },
    [navigate],
  );

  /**
   * Takes in the name -> tractor mapping from TractorContext.tsx and returns the number of tractors online.
   *
   * @param tractors - The tractors state from TractorContext.tsx.
   * @returns The number of tractors online.
   */
  const calculateNumTractorsOnline: (
    tractors: Map<string, Tractor>,
  ) => number = (tractors) => {
    return Array.from(tractors.values()).filter(
      (tractor) => tractor.statuses.online,
    ).length;
  };

  // Whenever we get new tractor data, recalculate the number online, number offline, and number total.
  const numTractorsOnline = useMemo(
    () => calculateNumTractorsOnline(tractors),
    [tractors],
  );
  const numTractorsTotal = useMemo(() => tractors.size, [tractors]);
  const numTractorsOffline = useMemo(
    () => numTractorsTotal - numTractorsOnline,
    [numTractorsTotal, numTractorsOnline],
  );

  // Reconstruct map whenever tractor data changes
  const mapVehicles = useMemo(
    () => constructMapVehicles(tractors),
    [tractors, constructMapVehicles],
  );

  const performanceIndicatorViews: View[] = [
    {
      name: "Today",
      dataSource: getTodayMetrics,
      params: [],
    },
    { name: "This Week", dataSource: getThisWeekMetrics, params: [] },
    { name: "All Time", dataSource: getLifetimeMetrics, params: [] },
  ];

  // Resolve once so the layout branch is stable across renders and we don't
  // call hasPermission twice with potentially different results.
  const canSeePerformanceIndicators = hasPermission("read:aerovect-vehicles");

  return (
    <FullPageContainer>
      <Navbar />
      <Right>
        <PageHeader>Dashboard</PageHeader>
        <Content>
          <TileContainer>
            <Flex
              $standalone={!canSeePerformanceIndicators}
              $grow={!canSeePerformanceIndicators}
            >
              <Statuses>
                <StatusRow>
                  <StatusName>Total Vehicles</StatusName>
                  <StatusValue>{numTractorsTotal}</StatusValue>
                </StatusRow>
                <StatusRow>
                  <StatusName>Online</StatusName>
                  <StatusValue>{numTractorsOnline}</StatusValue>
                </StatusRow>
                <StatusRow>
                  <StatusName>Offline</StatusName>
                  <StatusValue>{numTractorsOffline}</StatusValue>
                </StatusRow>
                <Tractors>
                  {Array.from(tractors.entries())
                    .sort(([, a], [, b]) => {
                      if (a.statuses.online !== b.statuses.online) {
                        return a.statuses.online ? -1 : 1;
                      }
                      return a.displayName.localeCompare(b.displayName);
                    })
                    .map(([name, tractor]) => (
                      <TldStatus
                        key={tractor.displayName}
                        label={tractor.displayName}
                        status={tractor.statuses.online ? "online" : "offline"}
                        action={() =>
                          navigate(`/vehicles/${tractor.displayName}`)
                        }
                      />
                    ))}
                </Tractors>
              </Statuses>
              {mapVehicles.length > 0 ? (
                <Map
                  center={atlMapCenter}
                  vehicles={mapVehicles}
                  bounds={atlMapBounds}
                  height={
                    isSmallScreen
                      ? Math.round(window.innerHeight * 0.4)
                      : undefined
                  }
                />
              ) : (
                <GreyedOutMap
                  center={atlMapCenter}
                  height={
                    isSmallScreen
                      ? Math.round(window.innerHeight * 0.4)
                      : undefined
                  }
                >
                  No vehicle locations available yet
                </GreyedOutMap>
              )}
            </Flex>

            {canSeePerformanceIndicators && (
              <MultiViewTable
                title="Performance Indicators"
                views={performanceIndicatorViews}
              />
            )}
          </TileContainer>
        </Content>
        <Footer />
      </Right>
    </FullPageContainer>
  );
};

export const FullPageContainer = styled.div`
  height: 100%;
  min-height: 100vh;
  width: 100%;
  display: flex;

  @media (max-width: 1032px) {
    flex-direction: column;
  }
`;

export const Right = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.colors.background};
  height: 100%;
  min-height: 100vh;
`;

export const Content = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  padding: 20px;
  flex: 1;
  min-height: 0;
  gap: 20px;
  overflow: hidden;
`;

export const TileContainer = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  border: 1px solid ${(props) => props.theme.colors.lightGrey};
  overflow: hidden;
`;

/*
 * Flex supports two independent concerns:
 *
 *   $standalone = no sibling below me inside the same TileContainer
 *                 → drop the bottom divider (it only separates me from a sibling)
 *                 → use auto height (no reason to reserve 60vh for a nonexistent sibling)
 *
 *   $grow       = fill the parent vertically
 *                 → used when the parent has empty space to fill (Dashboard when the
 *                   metrics table is absent). Not used on VehicleDetail, where the
 *                   map tile intentionally hugs its content and the page accepts
 *                   whitespace below.
 *
 * Default (no props) keeps the legacy 60vh + divider behavior used when the parent
 * has a table sibling that's going to fill the rest of the container.
 */
export const Flex = styled.div<{
  $standalone?: boolean;
  $grow?: boolean;
}>`
  display: flex;
  flex-direction: row;
  width: 100%;
  min-height: 0;

  border-bottom: ${(props) =>
    props.$standalone ? "none" : `1px solid ${props.theme.colors.lightGrey}`};

  height: ${(props) =>
    props.$grow ? "auto" : props.$standalone ? "auto" : "60vh"};
  flex: ${(props) => (props.$grow ? "1 1 auto" : "0 0 auto")};

  @media (max-width: 600px) {
    flex-direction: column;
    height: ${(props) =>
      props.$grow ? "auto" : props.$standalone ? "auto" : "50vh"};
    flex: ${(props) => (props.$grow ? "1 1 auto" : "0 0 auto")};
  }
`;

export const Statuses = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 10px;
  width: 30%;
  max-width: 300px;
  min-width: 200px;
  min-height: 0;
  border-right: 1px solid ${(props) => props.theme.colors.lightGrey};
  overflow: hidden;

  @media (max-width: 600px) {
    max-width: 100%;
    width: 100%;
    min-width: unset;
    max-height: 40vh;
    flex-direction: column;
    border: none;
    border-bottom: 1px solid ${(props) => props.theme.colors.lightGrey};
  }
`;

export const StatusRow = styled.div`
  padding: 20px 10px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${(props) => props.theme.colors.lightGrey};
`;

export const StatusName = styled.span`
  font-size: 18px;
  margin-right: 20px;
`;

export const StatusValue = styled.span`
  font-size: 20px;
  font-weight: ${(props) => props.theme.fontWeight.medium};
`;

const Tractors = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-content: flex-start;
  gap: 10px;
  padding: 20px 0;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

export default Dashboard;
